// src/components/ShipmentModal.js

import { useMemo } from 'react';
import { X, Package, Truck, MapPin, Calendar, Thermometer, DollarSign, Box, Clock, Activity } from 'lucide-react';
import { ComposedChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useTheme } from '../context/ThemeContext';

function ShipmentModal({ shipment, readings, excursions, onClose }) {
  const { colors } = useTheme();
  
  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  const shipmentReadings = useMemo(() => {
    if (!readings || !shipment) return [];
    const filtered = readings.filter(r => r.shipmentId === shipment.id);
    return filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [readings, shipment]);
  
  const shipmentExcursions = useMemo(() => {
    if (!excursions || !shipment) return [];
    return excursions.filter(e => e.shipmentId === shipment.id);
  }, [excursions, shipment]);

  // Sample readings if too many (for performance)
  const chartReadings = useMemo(() => {
    if (shipmentReadings.length <= 100) return shipmentReadings;
    const step = Math.ceil(shipmentReadings.length / 100);
    return shipmentReadings.filter((_, i) => i % step === 0);
  }, [shipmentReadings]);

  // NOW we can do conditional return - AFTER all hooks
  if (!shipment) return null;

  // Helper to get location display
  const getOrigin = () => {
    const city = shipment.originName || shipment.originCity || 'Unknown';
    const state = shipment.originState || '';
    return state ? `${city}, ${state}` : city;
  };

  const getDestination = () => {
    const city = shipment.destinationName || shipment.destinationCity || 'Unknown';
    const state = shipment.destinationState || '';
    return state ? `${city}, ${state}` : city;
  };

  const statusColor = {
    'Delivered': colors.success,
    'In Transit': colors.info,
    'Pending': colors.warning,
    'Delayed': colors.danger,
  }[shipment.status] || colors.textMuted;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const DetailBox = ({ icon: Icon, label, value, iconColor }) => (
    <div style={{ 
      background: colors.bgElevated, 
      borderRadius: '12px', 
      padding: '1rem' 
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem', 
        marginBottom: '0.5rem' 
      }}>
        <Icon size={14} color={iconColor || colors.textMuted} />
        <span style={{ 
          fontSize: '0.7rem', 
          color: colors.textMuted, 
          textTransform: 'uppercase', 
          letterSpacing: '0.05em' 
        }}>
          {label}
        </span>
      </div>
      <div style={{ 
        fontSize: '0.95rem', 
        fontWeight: 500, 
        color: colors.textPrimary 
      }}>
        {value}
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    const isOutOfRange = data.temperatureC > shipment.tempMax || data.temperatureC < shipment.tempMin;
    return (
      <div style={{ 
        background: colors.bgCard, 
        border: `1px solid ${colors.border}`, 
        borderRadius: '8px', 
        padding: '0.75rem', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)' 
      }}>
        <div style={{ 
          fontSize: '0.75rem', 
          color: colors.textMuted, 
          marginBottom: '0.25rem' 
        }}>
          {new Date(data.timestamp).toLocaleString()}
        </div>
        <div style={{ 
          fontSize: '0.9rem', 
          fontWeight: 600, 
          color: isOutOfRange ? colors.danger : colors.primary 
        }}>
          {data.temperatureC?.toFixed(1)}°C
        </div>
        {isOutOfRange && (
          <div style={{ 
            fontSize: '0.7rem', 
            color: colors.danger, 
            marginTop: '0.25rem' 
          }}>
            ⚠ Out of range
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'rgba(0,0,0,0.8)', 
        backdropFilter: 'blur(4px)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1000, 
        padding: '2rem' 
      }} 
      onClick={onClose}
    >
      <div 
        style={{ 
          background: colors.bgCard, 
          borderRadius: '20px', 
          width: '100%', 
          maxWidth: '900px', 
          maxHeight: '90vh', 
          overflow: 'auto', 
          border: `1px solid ${colors.border}` 
        }} 
        onClick={e => e.stopPropagation()}
      >
        
        {/* Header */}
        <div style={{ 
          padding: '1.5rem', 
          borderBottom: `1px solid ${colors.border}`, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          position: 'sticky', 
          top: 0, 
          background: colors.bgCard, 
          zIndex: 10 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px', 
              background: colors.primaryBg, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Package size={24} color={colors.primary} />
            </div>
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem', 
                flexWrap: 'wrap' 
              }}>
                <span style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 600, 
                  color: colors.textPrimary 
                }}>
                  {shipment.productName || 'Unknown Product'}
                </span>
                <span style={{ 
                  padding: '0.25rem 0.6rem', 
                  borderRadius: '6px', 
                  fontSize: '0.7rem', 
                  fontWeight: 600, 
                  background: `${statusColor}20`, 
                  color: statusColor 
                }}>
                  {shipment.status}
                </span>
                {shipment.hasExcursion && (
                  <span style={{ 
                    padding: '0.25rem 0.6rem', 
                    borderRadius: '6px', 
                    fontSize: '0.7rem', 
                    fontWeight: 600, 
                    background: colors.dangerBg, 
                    color: colors.danger 
                  }}>
                    {shipment.excursionSeverity} Excursion
                  </span>
                )}
              </div>
              <div style={{ 
                fontSize: '0.85rem', 
                color: colors.textMuted, 
                fontFamily: "'JetBrains Mono', monospace", 
                marginTop: '0.25rem' 
              }}>
                {shipment.id}
              </div>
            </div>
          </div>
          <button 
            onClick={onClose} 
            style={{ 
              background: colors.bgElevated, 
              border: `1px solid ${colors.border}`, 
              borderRadius: '8px', 
              padding: '0.5rem', 
              cursor: 'pointer', 
              color: colors.textSecondary, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.bgHover || colors.bgElevated;
              e.currentTarget.style.color = colors.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.bgElevated;
              e.currentTarget.style.color = colors.textSecondary;
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Details Grid */}
        <div style={{ padding: '1.5rem', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '1rem' 
          }}>
            <DetailBox 
              icon={Truck} 
              label="Carrier" 
              value={shipment.carrierName || 'N/A'} 
              iconColor={colors.info} 
            />
            <DetailBox 
              icon={MapPin} 
              label="Origin" 
              value={getOrigin()} 
              iconColor={colors.success} 
            />
            <DetailBox 
              icon={MapPin} 
              label="Destination" 
              value={getDestination()} 
              iconColor={colors.danger} 
            />
            <DetailBox 
              icon={Calendar} 
              label="Ship Date" 
              value={formatDate(shipment.shipDate)} 
              iconColor={colors.purple} 
            />
            <DetailBox 
              icon={Thermometer} 
              label="Temp Range" 
              value={`${shipment.tempMin}°C to ${shipment.tempMax}°C`} 
              iconColor={colors.info} 
            />
            <DetailBox 
              icon={DollarSign} 
              label="Total Value" 
              value={`$${(shipment.totalValue || 0).toLocaleString()}`} 
              iconColor={colors.success} 
            />
            <DetailBox 
              icon={Box} 
              label="Units" 
              value={`${shipment.units || 0} units`} 
              iconColor={colors.warning} 
            />
            <DetailBox 
              icon={Clock} 
              label="Transit Time" 
              value={`${shipment.transitHours || 0} hours`} 
              iconColor={colors.pink} 
            />
          </div>
        </div>

        {/* Temperature Chart */}
        <div style={{ padding: '1.5rem', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: '1rem' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={18} color={colors.primary} />
              <span style={{ 
                fontSize: '0.9rem', 
                fontWeight: 600, 
                color: colors.textPrimary 
              }}>
                Temperature Timeline
              </span>
            </div>
            <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
              {shipmentReadings.length} readings
              {chartReadings.length < shipmentReadings.length && ` (showing ${chartReadings.length} samples)`}
            </span>
          </div>
          
          {chartReadings.length > 0 ? (
            <>
              <div style={{ height: '280px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartReadings} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                    <defs>
                      <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={colors.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="timestamp" 
                      stroke={colors.textMuted} 
                      fontSize={10} 
                      tickLine={false} 
                      axisLine={{ stroke: colors.border }} 
                      tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} 
                      interval="preserveStartEnd" 
                    />
                    <YAxis 
                      stroke={colors.textMuted} 
                      fontSize={10} 
                      tickLine={false} 
                      axisLine={{ stroke: colors.border }} 
                      domain={[shipment.tempMin - 5, shipment.tempMax + 15]} 
                      tickFormatter={(v) => `${v}°`} 
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine 
                      y={shipment.tempMax} 
                      stroke={colors.danger} 
                      strokeDasharray="5 5" 
                      strokeWidth={2} 
                    />
                    <ReferenceLine 
                      y={shipment.tempMin} 
                      stroke={colors.info} 
                      strokeDasharray="5 5" 
                      strokeWidth={2} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="temperatureC" 
                      stroke={colors.primary} 
                      fill="url(#tempGradient)" 
                      strokeWidth={2} 
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '2rem', 
                marginTop: '0.75rem', 
                fontSize: '0.75rem', 
                color: colors.textMuted 
              }}>
                <span>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '20px', 
                    height: '2px', 
                    background: colors.danger, 
                    marginRight: '0.5rem', 
                    verticalAlign: 'middle' 
                  }}></span>
                  Max ({shipment.tempMax}°C)
                </span>
                <span>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '20px', 
                    height: '2px', 
                    background: colors.info, 
                    marginRight: '0.5rem', 
                    verticalAlign: 'middle' 
                  }}></span>
                  Min ({shipment.tempMin}°C)
                </span>
                <span>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '20px', 
                    height: '2px', 
                    background: colors.primary, 
                    marginRight: '0.5rem', 
                    verticalAlign: 'middle' 
                  }}></span>
                  Actual Temp
                </span>
              </div>
            </>
          ) : (
            <div style={{ 
              height: '200px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              color: colors.textMuted, 
              background: colors.bgSecondary, 
              borderRadius: '12px' 
            }}>
              No temperature readings available for this shipment
            </div>
          )}
        </div>

        {/* Excursions */}
        {shipmentExcursions.length > 0 && (
          <div style={{ padding: '1.5rem' }}>
            <div style={{ 
              fontSize: '0.9rem', 
              fontWeight: 600, 
              color: colors.textPrimary, 
              marginBottom: '1rem' 
            }}>
              Excursion Events ({shipmentExcursions.length})
            </div>
            {shipmentExcursions.map((exc, i) => {
              const severityColor = exc.severity === 'Critical' 
                ? colors.danger 
                : exc.severity === 'Major' 
                  ? colors.warning 
                  : colors.info;
              
              return (
                <div 
                  key={i} 
                  style={{ 
                    background: colors.bgElevated, 
                    borderRadius: '10px', 
                    padding: '1rem', 
                    marginBottom: '0.75rem', 
                    borderLeft: `3px solid ${severityColor}` 
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '0.5rem' 
                  }}>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      fontWeight: 600, 
                      color: severityColor, 
                      textTransform: 'uppercase' 
                    }}>
                      {exc.severity}
                    </span>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: colors.textMuted 
                    }}>
                      {exc.durationMinutes} min
                    </span>
                  </div>
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: colors.textSecondary 
                  }}>
                    Peak deviation: +{exc.maxDeviationC}°C • Root cause: {exc.rootCause}
                  </div>
                  {exc.valueAtRisk && (
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: colors.textMuted, 
                      marginTop: '0.35rem' 
                    }}>
                      Value at risk: ${exc.valueAtRisk.toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShipmentModal;